import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Autocomplete,
  Grid
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Print as PrintIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import timesheetService from '../services/timesheetService';
import projectsService from '../services/projectsService';
import usersService from '../services/usersService';
import { Project, WorkPackage, WorkUnit, User } from '../types';
import DateRangePicker from '../components/DateRangePicker';
import { getZoomCompensatedAutocompleteSlotProps } from '../utils/zoomCompensation';

interface Client {
  id: number;
  name: string;
  main_contact: string;
}

interface ReportRow {
  client: string;
  project: string;
  workDate: string;
  activity: string;
  hours: number;
  workPackage: string;
  user: string;
}

const TimesheetReportingPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b', abyssal: '#0a1622' };
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<ReportRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [workPackages, setWorkPackages] = useState<WorkPackage[]>([]);
  const [workUnits, setWorkUnits] = useState<WorkUnit[]>([]);

  const [selectedClient, setSelectedClient] = useState('');
  const [selectedUser, setSelectedUser] = useState<number | ''>('');
  const [selectedProject, setSelectedProject] = useState<number | ''>('');
  const [selectedWorkPackage, setSelectedWorkPackage] = useState<number | ''>('');
  const [selectedWorkUnit, setSelectedWorkUnit] = useState<number | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Load initial data (projects, clients, and users)
  useEffect(() => {
    loadProjects();
    loadClients();
    loadUsers();
  }, []);

  // Clear project selection when client changes
  useEffect(() => {
    if (selectedClient) {
      setSelectedProject('');
      setWorkPackages([]);
      setSelectedWorkPackage('');
      setWorkUnits([]);
      setSelectedWorkUnit('');
    }
  }, [selectedClient]);

  // Load work packages when project changes
  useEffect(() => {
    if (selectedProject) {
      loadWorkPackages(selectedProject as number);
    } else {
      setWorkPackages([]);
      setSelectedWorkPackage('');
      setWorkUnits([]);
      setSelectedWorkUnit('');
    }
  }, [selectedProject]);

  // Load work units when work package changes
  useEffect(() => {
    if (selectedWorkPackage) {
      loadWorkUnits(selectedProject as number, selectedWorkPackage as number);
    } else {
      setWorkUnits([]);
      setSelectedWorkUnit('');
    }
  }, [selectedWorkPackage]);

  const loadProjects = async () => {
    try {
      const data = await timesheetService.getTimesheetProjects(undefined, { includeClosed: true });
      setProjects(data);
    } catch (err) {
      console.error('Failed to load projects', err);
    }
  };

  const loadClients = async () => {
    try {
      const data = await timesheetService.getClients();
      setClients(data);
    } catch (err) {
      console.error('Failed to load clients', err);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await usersService.getUsers();
      setUsers(data);
    } catch (err) {
      console.error('Failed to load users', err);
    }
  };

  const loadWorkPackages = async (projectId: number) => {
    try {
      const data = await projectsService.getWorkPackages(projectId);
      setWorkPackages(data);
    } catch (err) {
      console.error('Failed to load work packages', err);
    }
  };

  const loadWorkUnits = async (projectId: number, wpId: number) => {
    try {
      // We can filter work units by work package from the full list if needed, 
      // or fetch specific ones. The service might return all for project.
      // Let's assume getWorkUnits returns all for project and we filter.
      const data = await projectsService.getWorkUnits(projectId);
      setWorkUnits(data.filter(wu => wu.work_package_id === wpId));
    } catch (err) {
      console.error('Failed to load work units', err);
    }
  };

  const generateReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: any = {};
      if (selectedClient) filters.client = selectedClient;
      if (selectedUser) filters.user_id = selectedUser;
      if (selectedProject) filters.project_id = selectedProject;
      if (selectedWorkPackage) filters.work_package_id = selectedWorkPackage;
      if (selectedWorkUnit) filters.work_unit_id = selectedWorkUnit;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      const data = await timesheetService.getTimesheetReport(filters);
      setReportData(data);
    } catch (err) {
      setError('Failed to generate report');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedClient('');
    setSelectedUser('');
    setSelectedProject('');
    setSelectedWorkPackage('');
    setSelectedWorkUnit('');
    setStartDate('');
    setEndDate('');
    setReportData([]);
  };

  const handlePrint = () => {
    const printContent = document.getElementById('printable-report');
    if (!printContent) return;

    const windowUrl = 'about:blank';
    const uniqueName = new Date().getTime();
    const windowName = 'Print' + uniqueName;
    const printWindow = window.open(windowUrl, windowName, 'left=50000,top=50000,width=0,height=0');

    if (printWindow) {
      // Create the complete document structure
      printWindow.document.open();
      printWindow.document.title = 'Timesheet Report';

      // Set document styles
      const style = printWindow.document.createElement('style');
      style.textContent = `
        body { font-family: Arial, sans-serif; font-size: 12px; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; }
        .header { margin-bottom: 20px; }
        .header h1 { margin: 0; font-size: 18px; }
        .total-row { font-weight: bold; background-color: #f9f9f9; }
        .small-text { font-size: 10px; }
        @media print {
          @page { margin: 1cm; }
        }
      `;
      printWindow.document.head.appendChild(style);

      // Create header
      const headerDiv = printWindow.document.createElement('div');
      headerDiv.className = 'header';
      const title = printWindow.document.createElement('h1');
      title.textContent = 'Timesheet Report';
      const date = printWindow.document.createElement('p');
      date.textContent = `Generated on: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
      headerDiv.appendChild(title);
      headerDiv.appendChild(date);

      // Copy the table content
      const tableContent = printWindow.document.createElement('div');
      tableContent.innerHTML = printContent.innerHTML;

      // Append to body
      printWindow.document.body.appendChild(headerDiv);
      printWindow.document.body.appendChild(tableContent);

      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const filters: any = {};
      if (selectedClient) filters.client = selectedClient;
      if (selectedProject) filters.project_id = selectedProject;
      if (selectedWorkPackage) filters.work_package_id = selectedWorkPackage;
      if (selectedWorkUnit) filters.work_unit_id = selectedWorkUnit;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      await timesheetService.downloadTimesheetReportPDF(filters);
    } catch (err) {
      setError('Failed to download PDF report');
      console.error(err);
    }
  };

  const handleDownloadXLSX = async () => {
    try {
      const filters: any = {};
      if (selectedClient) filters.client = selectedClient;
      if (selectedProject) filters.project_id = selectedProject;
      if (selectedWorkPackage) filters.work_package_id = selectedWorkPackage;
      if (selectedWorkUnit) filters.work_unit_id = selectedWorkUnit;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      await timesheetService.downloadTimesheetReportXLSX(filters);
    } catch (err) {
      setError('Failed to download XLSX report');
      console.error(err);
    }
  };

  // Calculate total hours
  const totalHours = reportData.reduce((sum, row) => sum + row.hours, 0);

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 1, boxShadow: 'none', border: `1px solid ${panelBorder}`, bgcolor: surfaceBg }}>
        <Typography variant="body2" gutterBottom display="flex" alignItems="center" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a' }}>
          <FilterListIcon sx={{ mr: 1, fontSize: '1rem', color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }} /> Report Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              size="small"
              options={clients}
              getOptionLabel={(option) => option.name}
              value={clients.find(c => c.name === selectedClient) || null}
              onChange={(_, newValue) => setSelectedClient(newValue?.name || '')}
              slotProps={getZoomCompensatedAutocompleteSlotProps()}
              renderInput={(params) => <TextField {...params} label="Client" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              size="small"
              options={users}
              getOptionLabel={(option) => option.name}
              value={users.find(u => u.id === selectedUser) || null}
              onChange={(_, newValue) => setSelectedUser(newValue?.id || '')}
              slotProps={getZoomCompensatedAutocompleteSlotProps()}
              renderInput={(params) => <TextField {...params} label="User" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              size="small"
              options={projects.filter(project => !selectedClient || project.client_name === selectedClient)}
              getOptionLabel={(option) => option.name}
              value={projects.find(p => p.id === selectedProject) || null}
              onChange={(_, newValue) => setSelectedProject(newValue?.id || '')}
              slotProps={getZoomCompensatedAutocompleteSlotProps()}
              renderInput={(params) => <TextField {...params} label="Project" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              size="small"
              options={workPackages}
              getOptionLabel={(option) => option.code}
              value={workPackages.find(wp => wp.id === selectedWorkPackage) || null}
              onChange={(_, newValue) => setSelectedWorkPackage(newValue?.id || '')}
              disabled={!selectedProject}
              slotProps={getZoomCompensatedAutocompleteSlotProps()}
              renderInput={(params) => <TextField {...params} label="Work Package" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              size="small"
              options={workUnits}
              getOptionLabel={(option) => option.code}
              value={workUnits.find(wu => wu.id === selectedWorkUnit) || null}
              onChange={(_, newValue) => setSelectedWorkUnit(newValue?.id || '')}
              disabled={!selectedWorkPackage}
              slotProps={getZoomCompensatedAutocompleteSlotProps()}
              renderInput={(params) => <TextField {...params} label="Work Unit" />}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={8}>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onRangeChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
              }}
            />
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              sx={{ borderRadius: 1, borderColor: panelBorder, color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.75rem' }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              onClick={generateReport}
              disabled={loading}
              sx={{ borderRadius: 1, fontWeight: 600, boxShadow: 'none', bgcolor: NAUTILUS.sand, color: NAUTILUS.deepOcean, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' } }}
            >
              {loading ? <CircularProgress size={24} /> : 'Generate Report'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1, border: `1px solid ${panelBorder}` }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {reportData.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 1, boxShadow: 'none', border: `1px solid ${panelBorder}`, bgcolor: surfaceBg }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a' }}>Report Results</Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPDF}
                sx={{ borderRadius: 1, borderColor: panelBorder, color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.75rem' }}
              >
                Download PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadXLSX}
                sx={{ borderRadius: 1, borderColor: panelBorder, color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.75rem' }}
              >
                Download XLSX
              </Button>
            </Box>
          </Box>

          <Box sx={{ mb: 2, p: 2, borderRadius: 1, border: `1px solid ${panelBorder}`, bgcolor: isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.deepOcean, 0.04) }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'monospace', color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }}>
              Grand Total: {totalHours.toFixed(2)} hours
            </Typography>
          </Box>

          <div id="printable-report">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a' }}>Client</TableCell>
                    <TableCell sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a' }}>Project</TableCell>
                    <TableCell sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a' }}>Work Package</TableCell>
                    <TableCell sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a' }}>User</TableCell>
                    <TableCell sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a' }}>Work Unit</TableCell>
                    <TableCell sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a' }}>Work Date</TableCell>
                    <TableCell align="right" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a' }}>Hours</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.client}</TableCell>
                      <TableCell>{row.project}</TableCell>
                      <TableCell>{row.workPackage}</TableCell>
                      <TableCell>{row.user}</TableCell>
                      <TableCell sx={{
                        fontSize: row.activity.length > 50 ? '0.75rem' : 'inherit',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 300
                      }}>
                        {row.activity}
                      </TableCell>
                      <TableCell>{new Date(row.workDate).toLocaleDateString()}</TableCell>
                      <TableCell align="right">{row.hours.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="total-row" sx={{ borderTop: `2px solid ${panelBorder}` }}>
                    <TableCell colSpan={6} align="right" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }}>Grand Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace', color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }}>{totalHours.toFixed(2)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Paper>
      )}
    </Box>
  );
};

export default TimesheetReportingPage;